import { useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDocumentTitle } from '@/hooks/useDocumentTitle';
import { useAyahsBySurah } from '@/hooks/useAyah';
import { useSurahList } from '@/hooks/useSurah';
import { Skeleton } from '@/components/ui/Skeleton';
import { cn } from '@/lib/utils/cn';
import { isDuaAyah } from '@/lib/quran/duaAyahs';
import type { Ayah } from '@/types/domain';

const ISTIADHAH = 'أعوذ بالله من الشيطان الرجيم';
const BISMILLAH = 'بِسْمِ اللَّهِ الرَّحْمَٰنِ الرَّحِيمِ';
const SURAH_TAWBAH = 9;
const TEXT_SIZE_STORAGE_KEY = 'quran-arabic-text-size';

type QuranTextSize = 'small' | 'normal' | 'big';

const TEXT_SIZE_OPTIONS: Array<{ value: QuranTextSize; label: string }> = [
  { value: 'small', label: 'Small' },
  { value: 'normal', label: 'Normal' },
  { value: 'big', label: 'Big' },
];

export function QuranArabic() {
  useDocumentTitle('Quran Arabic');

  const [searchParams, setSearchParams] = useSearchParams();
  const rawSurah = Number(searchParams.get('surah') ?? '1');
  const rawAyah = Number(searchParams.get('ayah') ?? '');
  const surahId = clamp(Number.isFinite(rawSurah) ? rawSurah : 1, 1, 114);
  const selectedAyahNumber =
    Number.isFinite(rawAyah) && rawAyah > 0 ? Math.floor(rawAyah) : null;

  const surahs = useSurahList();
  const ayahs = useAyahsBySurah(surahId);
  const selectedSurah =
    surahs.data?.find((surah) => surah.id === surahId) ?? null;
  const clampedAyahNumber = selectedSurah && selectedAyahNumber
    ? clamp(selectedAyahNumber, 1, selectedSurah.ayah_count)
    : null;
  const selectedAyah = clampedAyahNumber
    ? ayahs.data?.find((ayah) => ayah.ayah_number === clampedAyahNumber) ?? null
    : null;
  const visibleAyahs = clampedAyahNumber && selectedAyah
    ? [selectedAyah]
    : ayahs.data ?? [];

  const [surahDraft, setSurahDraft] = useState(String(surahId));
  const [ayahDraft, setAyahDraft] = useState(
    clampedAyahNumber ? String(clampedAyahNumber) : '',
  );
  const [textSize, setTextSize] = useState<QuranTextSize>(() => {
    if (typeof window === 'undefined') return 'normal';
    const saved = window.localStorage.getItem(TEXT_SIZE_STORAGE_KEY);
    return isQuranTextSize(saved) ? saved : 'normal';
  });
  const [showSajdahCue, setShowSajdahCue] = useState(false);
  const highlightedAyahRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    setSurahDraft(String(surahId));
    setAyahDraft(clampedAyahNumber ? String(clampedAyahNumber) : '');
  }, [surahId, clampedAyahNumber]);

  useEffect(() => {
    window.localStorage.setItem(TEXT_SIZE_STORAGE_KEY, textSize);
  }, [textSize]);

  useEffect(() => {
    if (!selectedAyah?.sajda) {
      setShowSajdahCue(false);
      return;
    }
    setShowSajdahCue(true);
    const timer = window.setTimeout(() => setShowSajdahCue(false), 3000);
    return () => window.clearTimeout(timer);
  }, [selectedAyah?.surah_id, selectedAyah?.ayah_number, selectedAyah?.sajda]);

  useEffect(() => {
    if (!clampedAyahNumber || !highlightedAyahRef.current) return;
    highlightedAyahRef.current.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
    });
  }, [clampedAyahNumber, ayahs.isSuccess]);

  const openingText = useMemo(
    () =>
      surahId === SURAH_TAWBAH
        ? ISTIADHAH
        : `${ISTIADHAH}\n${BISMILLAH}`,
    [surahId],
  );

  function applyFilters() {
    const nextSurah = clamp(Number(surahDraft) || 1, 1, 114);
    const surah = surahs.data?.find((item) => item.id === nextSurah);
    const ayahValue = ayahDraft.trim() ? Number(ayahDraft) : null;
    const nextAyah = surah && ayahValue
      ? clamp(Math.floor(ayahValue), 1, surah.ayah_count)
      : null;

    const next = new URLSearchParams();
    next.set('surah', String(nextSurah));
    if (nextAyah) next.set('ayah', String(nextAyah));
    setSearchParams(next, { replace: false });
  }

  function selectSurah(nextSurahId: string) {
    setSurahDraft(nextSurahId);
    setAyahDraft('');
    const nextSurah = clamp(Number(nextSurahId) || 1, 1, 114);
    const next = new URLSearchParams();
    next.set('surah', String(nextSurah));
    setSearchParams(next, { replace: false });
  }

  function clearAyah() {
    const next = new URLSearchParams();
    next.set('surah', String(surahId));
    setSearchParams(next, { replace: false });
  }

  return (
    <main className="min-h-[calc(100dvh-4rem)] bg-ink-50 text-ink-900 dark:bg-night-900 dark:text-night-50">
      <section className="mx-auto flex min-h-[calc(100dvh-4rem)] w-full max-w-7xl flex-col px-3 py-3 sm:px-5 sm:py-5">
        <header className="rounded-2xl border border-ink-200/80 bg-ink-50/92 p-3 shadow-sm backdrop-blur-xl dark:border-night-500/30 dark:bg-night-900/92 lg:sticky lg:top-20 lg:z-20">
          <div className="flex flex-col gap-3 lg:flex-row lg:items-end lg:justify-between">
            <div>
              <h1 className="text-xl font-bold text-ink-950 dark:text-night-50">
                Quran Arabic
              </h1>
              <p className="mt-1 hidden text-sm text-ink-500 dark:text-night-200 sm:block">
                Select a Surah, optionally enter an ayah number, then read in Arabic only.
              </p>
            </div>
            <div className="grid gap-3 xl:min-w-[680px]">
              <form
                className="grid gap-2 sm:grid-cols-[minmax(0,1.4fr)_minmax(7rem,0.55fr)_auto_auto]"
                onSubmit={(event) => {
                  event.preventDefault();
                  applyFilters();
                }}
              >
                <label className="grid gap-1 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-night-200">
                  Surah
                  <select
                    value={surahDraft}
                    onChange={(event) => selectSurah(event.target.value)}
                    disabled={surahs.isLoading || !!surahs.error}
                    className="h-11 rounded-xl border border-ink-200 bg-white px-3 text-sm font-semibold text-ink-900 outline-none transition-colors focus:border-accent dark:border-night-500/40 dark:bg-night-800 dark:text-night-50"
                  >
                    {surahs.isLoading && <option>Loading Surahs...</option>}
                    {(surahs.data ?? []).map((surah) => (
                      <option key={surah.id} value={surah.id}>
                        {surah.id}. {surah.name_en} - {surah.name_ar}
                      </option>
                    ))}
                  </select>
                </label>
                <label className="grid gap-1 text-xs font-semibold uppercase tracking-wide text-ink-500 dark:text-night-200">
                  Ayah
                  <input
                    inputMode="numeric"
                    min={1}
                    max={selectedSurah?.ayah_count ?? undefined}
                    placeholder="All"
                    type="number"
                    value={ayahDraft}
                    onChange={(event) => setAyahDraft(event.target.value)}
                    className="h-11 rounded-xl border border-ink-200 bg-white px-3 text-sm font-semibold text-ink-900 outline-none transition-colors focus:border-accent dark:border-night-500/40 dark:bg-night-800 dark:text-night-50"
                  />
                </label>
                <button
                  type="submit"
                  className="h-11 rounded-xl bg-accent px-4 text-sm font-bold text-white transition-colors hover:bg-accent-600 dark:bg-beam dark:text-beam-ink dark:hover:bg-beam-pale"
                >
                  Go
                </button>
                <button
                  type="button"
                  onClick={clearAyah}
                  className="h-11 rounded-xl border border-ink-200 px-4 text-sm font-bold text-ink-700 transition-colors hover:border-accent/50 hover:text-accent dark:border-night-500/40 dark:text-night-100 dark:hover:border-beam/50 dark:hover:text-beam"
                >
                  Full Surah
                </button>
              </form>
              <div
                className="inline-grid grid-cols-3 gap-1 rounded-xl border border-ink-200 bg-white p-1 dark:border-night-500/40 dark:bg-night-800"
                aria-label="Arabic text size"
                role="group"
              >
                {TEXT_SIZE_OPTIONS.map((option) => (
                  <button
                    key={option.value}
                    type="button"
                    onClick={() => setTextSize(option.value)}
                    aria-pressed={textSize === option.value}
                    className={cn(
                      'h-9 rounded-lg px-3 text-xs font-bold transition-colors',
                      textSize === option.value
                        ? 'bg-accent text-white dark:bg-beam dark:text-beam-ink'
                        : 'text-ink-600 hover:bg-ink-100 dark:text-night-200 dark:hover:bg-night-700',
                    )}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </header>

        <section className="mt-4 flex-1 rounded-3xl border border-ink-200/80 bg-white/72 px-4 py-6 shadow-sm dark:border-night-500/30 dark:bg-night-950/45 sm:px-7 lg:px-12">
          {surahs.error || ayahs.error ? (
            <div className="rounded-2xl border border-red-300/60 bg-red-50 p-5 text-sm font-semibold text-red-700 dark:border-red-400/30 dark:bg-red-500/10 dark:text-red-200">
              Quran data could not load. Please refresh the page.
            </div>
          ) : surahs.isLoading || ayahs.isLoading ? (
            <QuranSkeleton />
          ) : selectedSurah ? (
            <>
              <div className="mb-7 flex flex-wrap items-center justify-between gap-3 border-b border-ink-200 pb-5 dark:border-night-500/30">
                <div>
                  <p className="text-sm font-semibold text-accent dark:text-beam">
                    Surah {selectedSurah.id}
                  </p>
                  <h2 className="mt-1 text-2xl font-bold text-ink-950 dark:text-night-50">
                    {selectedSurah.name_en}
                  </h2>
                </div>
                <p lang="ar" dir="rtl" className="quran-viewer-surah-name">
                  {selectedSurah.name_ar}
                </p>
              </div>

              <div className="mb-8 rounded-2xl border border-accent/20 bg-accent-50/70 p-5 text-center dark:border-beam/20 dark:bg-beam/10">
                <p lang="ar" dir="rtl" className="quran-viewer-opening whitespace-pre-line">
                  {openingText}
                </p>
              </div>

              <div className={cn('space-y-5', 'quran-text-' + textSize)}>
                {visibleAyahs.map((ayah) => (
                  <QuranAyahLine
                    key={ayah.id}
                    ayah={ayah}
                    isSelected={ayah.ayah_number === clampedAyahNumber}
                    refCallback={
                      ayah.ayah_number === clampedAyahNumber
                        ? (node) => {
                            highlightedAyahRef.current = node;
                          }
                        : undefined
                    }
                  />
                ))}
              </div>
            </>
          ) : (
            <p className="text-sm text-ink-500 dark:text-night-200">
              Select a Surah to begin.
            </p>
          )}
        </section>
      </section>
      {showSajdahCue && <SajdahGifCue />}
    </main>
  );
}

function QuranAyahLine({
  ayah,
  isSelected,
  refCallback,
}: {
  ayah: Ayah;
  isSelected: boolean;
  refCallback?: (node: HTMLDivElement | null) => void;
}) {
  const isDua = isDuaAyah(ayah.surah_id, ayah.ayah_number);

  return (
    <div
      ref={refCallback}
      className={cn(
        'rounded-2xl px-3 py-3 transition-colors sm:px-5',
        isSelected
          ? 'bg-accent-50 ring-1 ring-accent/25 dark:bg-beam/10 dark:ring-beam/25'
          : 'hover:bg-ink-100/60 dark:hover:bg-night-800/45',
      )}
    >
      <p lang="ar" dir="rtl" className="quran-viewer-arabic">
        <span className={cn(isDua && 'dua-highlight')}>{ayah.text_ar}</span>
        <span className="quran-viewer-ayah-pill" aria-label={`Ayah ${ayah.ayah_number}`}>
          {ayah.ayah_number}
        </span>
        {ayah.sajda && <span className="quran-viewer-sajdah-pill">سجدة</span>}
      </p>
    </div>
  );
}

function SajdahGifCue() {
  return (
    <div className="pointer-events-none fixed inset-0 z-50 flex items-center justify-center bg-night-950/25 px-4 backdrop-blur-sm">
      <div className="quran-sajdah-gif pointer-events-auto rounded-3xl border border-amber-300/35 bg-night-900/95 px-8 py-7 text-center shadow-[0_30px_90px_rgba(0,0,0,0.45)]">
        <div className="quran-sajdah-bow mx-auto mb-4 flex h-20 w-20 items-center justify-center rounded-full bg-amber-300/15 text-4xl">
          ۩
        </div>
        <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-200">
          Sajdah
        </p>
        <p lang="ar" dir="rtl" className="mt-2 text-3xl font-bold text-night-50">
          سجدة
        </p>
      </div>
    </div>
  );
}

function QuranSkeleton() {
  return (
    <div className="space-y-5">
      <Skeleton className="ml-auto h-12 w-2/3" />
      <Skeleton className="h-24 w-full" />
      <Skeleton className="ml-auto h-12 w-5/6" />
      <Skeleton className="ml-auto h-12 w-3/4" />
    </div>
  );
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(Math.max(value, min), max);
}

function isQuranTextSize(value: string | null): value is QuranTextSize {
  return value === 'small' || value === 'normal' || value === 'big';
}
